import { getAuthHeaders } from './auth';

interface APIRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
}

export async function makeAPIRequest(
  endpoint: string,
  options: APIRequestOptions = {}
) {
  const {
    method = 'GET',
    body,
    headers = {},
  } = options;

  const url = endpoint.startsWith('http') ? endpoint : `${import.meta.env.VITE_API_URL}${endpoint}`;

  let finalHeaders: Record<string, string> = {
    ...getAuthHeaders(),
    ...headers,
  };

  let requestOptions: RequestInit = {
    method,
    headers: finalHeaders,
  };

  if (body) {
    if (body instanceof FormData) {
      requestOptions.body = body;
      // Let browser set Content-Type for FormData
      delete (requestOptions.headers as any)['Content-Type'];
    } else if (typeof body === 'string') {
      requestOptions.body = body;
      requestOptions.headers = {
        'Content-Type': 'text/plain',
        ...finalHeaders,
      };
    } else {
      requestOptions.body = JSON.stringify(body);
      requestOptions.headers = {
        'Content-Type': 'application/json',
        ...finalHeaders,
      };
    }
  }

  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    const errorData = await response.json();
    (error as any).message = errorData.message;
    (error as any).status = response.status;
    (error as any).data = errorData;
    (error as any).response = response;
    throw error;
  }

  return response;
}

// Convenience functions for common HTTP methods
export const api = {
  get: (endpoint: string, options?: Omit<APIRequestOptions, 'method'>) => 
    makeAPIRequest(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint: string, body?: any, options?: Omit<APIRequestOptions, 'method' | 'body'>) => 
    makeAPIRequest(endpoint, { ...options, method: 'POST', body }),
  
  put: (endpoint: string, body?: any, options?: Omit<APIRequestOptions, 'method' | 'body'>) => 
    makeAPIRequest(endpoint, { ...options, method: 'PUT', body }),
  
  delete: (endpoint: string, options?: Omit<APIRequestOptions, 'method'>) => 
    makeAPIRequest(endpoint, { ...options, method: 'DELETE' }),
  
  patch: (endpoint: string, body?: any, options?: Omit<APIRequestOptions, 'method' | 'body'>) => 
    makeAPIRequest(endpoint, { ...options, method: 'PATCH', body }),
}; 